import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressTracker from '../../components/navigation/ProgressTracker';
import ContextualActionController from '../../components/navigation/ContextualActionController';
import { useAssessment } from '../../components/navigation/AssessmentStateManager';
import ValueProposition from './components/ValueProposition';
import ProgressSummary from './components/ProgressSummary';
import { getTrackingParams } from '../../utils/trackingParams';

const GOHIGHLEVEL_WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/h7XdTJvoWUlKkvHWSPqM/webhook-trigger/2gLWV5OSmr4miiplPRP3';

const EmailCapture = () => {
  const navigate = useNavigate();
  const { state, submitEmail, setScreen } = useAssessment();
  
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setScreen('email-capture');
  }, [setScreen]);

  const validateEmail = () => {
    const trimmedEmail = email?.trim();
    
    if (!trimmedEmail) {
      setEmailError('Email is required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return false;
    }

    setEmailError('');
    return true;
  };

  const sendToGoHighLevel = async (assessmentData) => {
    try {
      const response = await fetch(GOHIGHLEVEL_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(assessmentData)
      });

      if (!response?.ok) {
        console.error('GoHighLevel webhook failed:', response?.status, response?.statusText);
      } else {
        console.log('Successfully sent data to GoHighLevel');
      }
    } catch (error) {
      console.error('Error sending to GoHighLevel:', error);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateEmail()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Use archetype scores already calculated in state
      const archetypeScores = state?.archetypeScores || {};
      const sortedArchetypes = Object.entries(archetypeScores)?.sort(([, a], [, b]) => b - a);
      const dominantArchetype = sortedArchetypes?.[0]?.[0] || 'unknown';
      const secondaryArchetype = sortedArchetypes?.[1]?.[0] || 'unknown';

      // Get Facebook tracking parameters
      const trackingParams = getTrackingParams();

      // Prepare data for GoHighLevel (includes tracking params for Conversion API)
      const assessmentData = {
        email: email?.trim(),
        dominant_archetype: dominantArchetype,
        secondary_archetype: secondaryArchetype,
        archetype_scores: archetypeScores,
        responses: state?.responses,
        timestamp: new Date()?.toISOString(),
        source: 'false-self-assessment',
        // Facebook tracking parameters for GHL Conversion API
        facebook_tracking: {
          fbclid: trackingParams?.fbclid,
          fbp: trackingParams?.fbp,
          fbc: trackingParams?.fbc,
          event_source_url: window?.location?.href,
          user_agent: navigator?.userAgent
        },
        // UTM tracking parameters for campaign attribution (flattened to top level)
        utm_source: trackingParams?.utm_source,
        utm_medium: trackingParams?.utm_medium,
        utm_campaign: trackingParams?.utm_campaign,
        utm_content: trackingParams?.utm_content,
        utm_term: trackingParams?.utm_term
      };

      // Send to GoHighLevel webhook (GHL will fire Lead event to Facebook)
      await sendToGoHighLevel(assessmentData);

      // Conversion tracking handled by GHL server-side
      // Facebook Pixel conversion events removed to prevent double counting

      // Store email and archetype scores in state
      submitEmail(email?.trim(), archetypeScores);

      // Redirect to paid quiz loader with all tracking parameters
      const redirectUrl = new URL('https://limitlessmen.com/quiz-loader');
      
      // Add email parameter
      redirectUrl?.searchParams?.set('email', email?.trim());
      
      // Add Facebook tracking parameters
      if (trackingParams?.fbclid) {
        redirectUrl?.searchParams?.set('fbclid', trackingParams?.fbclid);
      }
      if (trackingParams?.fbp) {
        redirectUrl?.searchParams?.set('fbp', trackingParams?.fbp);
      }
      if (trackingParams?.fbc) {
        redirectUrl?.searchParams?.set('fbc', trackingParams?.fbc);
      }
      
      // Add UTM parameters
      if (trackingParams?.utm_source) {
        redirectUrl?.searchParams?.set('utm_source', trackingParams?.utm_source);
      }
      if (trackingParams?.utm_medium) {
        redirectUrl?.searchParams?.set('utm_medium', trackingParams?.utm_medium);
      }
      if (trackingParams?.utm_campaign) {
        redirectUrl?.searchParams?.set('utm_campaign', trackingParams?.utm_campaign);
      }
      if (trackingParams?.utm_content) {
        redirectUrl?.searchParams?.set('utm_content', trackingParams?.utm_content);
      }
      if (trackingParams?.utm_term) {
        redirectUrl?.searchParams?.set('utm_term', trackingParams?.utm_term);
      }
      
      // Add archetype scores as query parameters
      if (archetypeScores?.performer !== undefined) {
        redirectUrl?.searchParams?.set('performer', archetypeScores?.performer);
      }
      if (archetypeScores?.controller !== undefined) {
        redirectUrl?.searchParams?.set('controller', archetypeScores?.controller);
      }
      if (archetypeScores?.pleaser !== undefined) {
        redirectUrl?.searchParams?.set('pleaser', archetypeScores?.pleaser);
      }
      if (archetypeScores?.ghost !== undefined) {
        redirectUrl?.searchParams?.set('ghost', archetypeScores?.ghost);
      }
      if (archetypeScores?.destroyer !== undefined) {
        redirectUrl?.searchParams?.set('destroyer', archetypeScores?.destroyer);
      }
      
      // Redirect to external URL
      window.location.href = redirectUrl?.toString();
    } catch (error) {
      console.error('Error submitting assessment:', error);
      setEmailError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/question-assessment');
  };

  const isValid = email?.trim() !== '' && !emailError;

  return (
    <div className="min-h-screen bg-brand-black">
      <ProgressTracker
        currentScreen="email-capture"
        completionPercentage={95}
      />

      <main className="pt-20 md:pt-24 pb-32 md:pb-36 px-4 md:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <ValueProposition 
            email={email}
            onEmailChange={setEmail}
            emailError={emailError}
            onValidate={validateEmail}
          />
          
          <ProgressSummary questionsCompleted={state?.totalQuestions} />
        </div>
      </main>

      <ContextualActionController
        currentScreen="email-capture"
        isValid={isValid}
        isLoading={isSubmitting}
        onAction={handleSubmit}
        showBackButton={true}
        onBack={handleBack}
        secondaryText="By continuing, you agree to receive your assessment results via email"
      />
    </div>
  );
};

export default EmailCapture;